import { policyDocs } from '@/common/CanceledPolicy';
import { PolicyUi, VuexState } from '@/common/Policy';
import { User } from '@/common/Users';
import Brand from '@/services/Brand';
import Policy from '@/services/Policy';
import { pruneEmpty } from '@/utils/jsHelpers';
import { ActionContext } from 'vuex';
import moment from 'moment';
import store from './';

type RootState = unknown;

const state: VuexState = {
	balanceUiLoading: false,
	policies: [],
	policy_number: null,
};

const getters = {
	balanceUiLoading: (state: VuexState) => state.balanceUiLoading,

	policy_number: (state: VuexState) => state.policy_number,

	policies: (state: VuexState, getters: any): PolicyUi[] => state.policies,

	thirtyDayPolicies: (state: VuexState): PolicyUi[] => {
		const policies = state.policies;

		if (policies.length <= 0) {
			return [];
		}

		const brand = Brand.getBrand();
		const coreUser: User = store.state.Auth.coreUser;
		const user: User = store.state.Auth.user;
		const isAgent = user.isAgent === 'true' || user.isAgent === true;
		const userPolicies = coreUser?.policies?.[brand] || [];

		return policies
			.map(policy => {
				try {
					/**
					 * Added this new rule so if a policy was manually added to an account,
					 * they would still be able to access loss reports and docs.
					 */
					const getPolicyFromUserList = userPolicies.find(
						p => p.policy_number === policy.policy_number,
					);
					if (isAgent && getPolicyFromUserList?.manually_added === true) {
						return policy;
					}

					return Policy.canceledPolicy(policy, policyDocs);
				} catch (e) {
					console.log(e);
				}
			})
			.filter(p => p !== undefined)
			.sort((left: PolicyUi, right: PolicyUi): number =>
				left.dropDownPolicyHolderName.localeCompare(right.dropDownPolicyHolderName),
			);
	},

	activePolicies: (state: VuexState): PolicyUi[] =>
		state.policies
			.filter(policy => {
				// check if cancelled + reasoning is CANSUNLIGHT, AMALRRG or AMALGRRG + 1 year from effective date
				// should behave the same as an active policy
				const isCancelledInSunlightOrAmalrrg = (policyStatus, statusReason) => {
					return policyStatus === 'CANCEL' && ['CANSUNLGHT', 'AMALRRG', 'AMALGRRG'].includes(statusReason);
				} 
				const getTimeDifference = (effectiveDate) => moment().diff(moment(effectiveDate), 'years', true);

				return policy?.policy_status === 'ACTIVE' || ( isCancelledInSunlightOrAmalrrg(policy?.policy_status, policy?.policy_status_reason) && getTimeDifference(policy?.effective_to_date) <= 1 );
			})
			.sort((left: PolicyUi, right: PolicyUi): number =>
				left.dropDownPolicyHolderName.localeCompare(right.dropDownPolicyHolderName),
			),

	policiesSortedByDropDownName: (state: VuexState, getters: any): PolicyUi[] =>
		getters.policies
			.slice()
			.sort((left: PolicyUi, right: PolicyUi): number =>
				left.dropDownPolicyHolderName.localeCompare(right.dropDownPolicyHolderName),
			),

	policiesSortedByPolicyNumber: (state: VuexState, getters: any): PolicyUi[] =>
		getters.policies
			.slice()
			.sort((left: PolicyUi, right: PolicyUi): number =>
				left.policy_number.localeCompare(right.policy_number),
			),

	getPolicyById: (state: VuexState) => (policy_number: string) =>
		state.policies.find(policy => policy.policy_number === policy_number),
};

const mutations = {
	setBalanceUiLoading: (state: VuexState, balanceUiLoading: boolean): void => {
		state.balanceUiLoading = balanceUiLoading;
	},
	setPolicies: (state: VuexState, policies: PolicyUi[]): void => {
		state.policies = policies;
	},
	setPolicyNumber: (state: VuexState, policyNumber: string): void => {
		state.policy_number = policyNumber;
	},
};

const actions = {
	clearData: ({ commit }: ActionContext<VuexState, RootState>): void => {
		commit('setBalanceUiLoading', false);
		commit('setPolicies', []);
		commit('setPolicyNumber', null);
	},
	setBalanceUiLoading: (
		{ commit }: ActionContext<VuexState, RootState>,
		payload: boolean,
	): void => {
		commit('setBalanceUiLoading', payload);
	},
	setPolicyData: ({ commit }: ActionContext<VuexState, RootState>, payload: PolicyUi[]): void => {
		if (payload === null) {
			return;
		}

		payload = pruneEmpty(payload);

		commit('setPolicies', payload);
	},
	setSelectedPolicy: ({ commit }: ActionContext<VuexState, RootState>, payload: string): void => {
		if (payload === null) {
			return;
		}

		commit('setPolicyNumber', payload);
	},
};

export { state, getters, mutations, actions };
